import { useNamespaceCopy } from '@/i18n/ns';
import appsNavCopy, { type AppsNavCopyKey } from '@/i18n/ns/apps-nav';
import { useWorkspace } from '../context';
import { NewObjectCanvas } from '../primitives';
import { TemplateGalleryFrame } from '../template-gallery';
import { StartingPointCard } from '../welcome-suggestion';
import './app-create.css';

// The App ideas are starting points. On the Apps page each opens the new-App chat with its whole description in the
// composer; under that chat's composer each sends it, as every starting point does. They stay beside a composer that
// already holds words ("Build me an App that "), so they are the welcome footer's, not the suggestions that fold away.

const IDEAS: readonly (readonly [string, AppsNavCopyKey, AppsNavCopyKey])[] = [
  ['prices', 'appsIdeaPrices', 'appsIdeaPricesDraft'],
  ['stock', 'appsIdeaStock', 'appsIdeaStockDraft'],
  ['compare', 'appsIdeaCompare', 'appsIdeaCompareDraft'],
  ['returns', 'appsIdeaReturns', 'appsIdeaReturnsDraft'],
];

/** The App ideas in the reader's language: a name, and the description that is drafted or sent. */
export function useAppIdeas() {
  const copy = useNamespaceCopy(appsNavCopy);
  return IDEAS.map(([id, name, draft]) => ({
    id: `app-idea-${id}`,
    title: copy[name],
    text: copy[draft],
  }));
}

/** "Start from an idea" on the Apps page: the Skills template section, holding the new chat's starting-point cards. */
export function AppIdeas({ onSelect }: { onSelect: (draft: string) => void }) {
  const copy = useNamespaceCopy(appsNavCopy);
  return (
    <TemplateGalleryFrame
      heading={copy.appsIdeasTitle}
      intro={copy.appsIdeasIntro}
      className="ap-ideas"
    >
      {useAppIdeas().map((idea) => (
        <StartingPointCard
          key={idea.id}
          icon="app"
          title={idea.title}
          description={idea.text}
          onSelect={() => onSelect(idea.text)}
        />
      ))}
    </TemplateGalleryFrame>
  );
}

/** A new App's panel before the chat has built anything (canvas=app-preview&id=new), as a new Skill's. */
export function NewAppCanvas() {
  const copy = useNamespaceCopy(appsNavCopy);
  return <NewObjectCanvas title={copy.appsNewCanvasTitle} body={copy.appsNewCanvasBody} />;
}

/** A new App's chat: its ideas under the composer, the new chat's starting-point cards. One sends its description in
 * this chat, the App panel kept beside it. */
export function NewAppStarters() {
  const ws = useWorkspace();
  return (
    <div className="aui-thread-welcome-suggestions">
      {useAppIdeas().map((idea) => (
        <StartingPointCard
          key={idea.id}
          icon="app"
          title={idea.title}
          description={idea.text}
          onSelect={() => ws.send(idea.text, ws.turnContext, true, ws.canvas)}
        />
      ))}
    </div>
  );
}
